import { useState, useEffect, useRef } from "react";
import { getPageStatuses, getFeatureFlags } from "../utils/adminStore";
import { navigate } from "../hooks/useRoute";

export interface SearchItem {
  id: string;
  title: string;
  category: "Services" | "Courses" | "Pricing" | "Company" | "Policies" | "Blog & Publications" | "News & Dispatches";
  description: string;
  url: string;
  keywords: string[];
}

export const searchableItems: SearchItem[] = [
  // Services
  {
    id: "srv-it",
    title: "IT & ITeS Services",
    category: "Services",
    description: "Software development, technical consultancy, systems integration, and networking infrastructure.",
    url: "/services#it-ites",
    keywords: ["it", "ites", "software development", "systems integration", "networking", "consultancy", "infrastructure", "api"],
  },
  {
    id: "srv-saas",
    title: "SaaS & Digital Platforms",
    category: "Services",
    description: "Design, architecture, licensing, and scaling of SaaS products, dynamic CMS websites, and cloud platforms.",
    url: "/services#saas-platforms",
    keywords: ["saas", "cms", "digital platforms", "web application", "cloud hosting", "multi-tenant", "e-commerce"],
  },
  {
    id: "srv-rnd",
    title: "Research & Development (R&D)",
    category: "Services",
    description: "Applied research, hardware prototyping, embedded microcontrollers, IoT sensor telemetry, and technical infrastructure.",
    url: "/services#rnd-hardware",
    keywords: ["rnd", "research", "prototyping", "hardware", "firmware", "microcontroller", "embedded", "electronics", "iot", "internet of things", "telemetry"],
  },
  {
    id: "srv-robotics",
    title: "Robotics & Automation",
    category: "Services",
    description: "Engineering design, assembly, microcontrollers, Industrial IoT (IIoT), and maintenance of intelligent robotics & control systems.",
    url: "/services#robotics-automation",
    keywords: ["robotics", "automation", "control systems", "electronics", "assembly", "sensors", "microcontroller", "iiot", "industrial iot", "scada", "plc"],
  },
  {
    id: "srv-security",
    title: "Security & Surveillance Systems",
    category: "Services",
    description: "Smart IP surveillance, IoT biometric access control, firewalls, and security infrastructure for commercial operations.",
    url: "/services#security-systems",
    keywords: ["security systems", "surveillance", "cctv", "biometric", "access control", "firewall", "ip camera", "smart security", "iot security"],
  },
  {
    id: "srv-training",
    title: "Technology Training & Education",
    category: "Services",
    description: "Enterprise developer upskilling, corporate technology training programs, and institutional engineering education in Full-Stack, IoT & Embedded systems.",
    url: "/services#training-education",
    keywords: ["training", "education", "corporate training", "upskilling", "engineering education", "consultancy", "professional development", "iot training", "embedded"],
  },

  // Courses
  {
    id: "crs-python-fullstack",
    title: "Python Full-Stack Web Development Course",
    category: "Courses",
    description: "Comprehensive 12-week track covering Python 3, Django, FastAPI, PostgreSQL, REST APIs, and full deployment.",
    url: "/courses#python-fullstack",
    keywords: ["python", "django", "fastapi", "fullstack", "postgresql", "rest api", "course", "learn python"],
  },
  {
    id: "crs-mern",
    title: "MERN Stack Web Development Course",
    category: "Courses",
    description: "Master modern full-stack web development using MongoDB, Express.js, React 18, and Node.js with TypeScript.",
    url: "/courses#mern-stack",
    keywords: ["mern", "react", "nodejs", "mongodb", "express", "fullstack", "course"],
  },
  {
    id: "crs-mean",
    title: "MEAN Stack Web Development Course",
    category: "Courses",
    description: "Enterprise full-stack engineering course covering Angular framework, Node.js, Express.js, and MongoDB.",
    url: "/courses#mean-stack",
    keywords: ["mean", "angular", "typescript", "nodejs", "mongodb", "enterprise", "course"],
  },
  {
    id: "crs-qa",
    title: "Software Testing & Quality Assurance Course",
    category: "Courses",
    description: "Manual testing methodologies, automated web testing with Selenium & Playwright, API testing, and CI/CD defect tracking.",
    url: "/courses#software-testing",
    keywords: ["testing", "qa", "selenium", "playwright", "automation", "postman", "course"],
  },
  {
    id: "crs-python-auto",
    title: "Python Programming & Automation Scripting",
    category: "Courses",
    description: "Master Python programming syntax, automated workflow scripts, web scraping, and command-line utilities.",
    url: "/courses#python-automation",
    keywords: ["python", "scripting", "automation", "web scraping", "beautifulsoup", "selenium", "course"],
  },
  {
    id: "crs-python-ds",
    title: "Data Science & Machine Learning with Python",
    category: "Courses",
    description: "NumPy, Pandas, Exploratory Data Analysis, Machine Learning with Scikit-learn, and model deployment.",
    url: "/courses#python-data-science",
    keywords: ["data science", "machine learning", "numpy", "pandas", "scikit-learn", "eda", "ml", "python", "course"],
  },
  {
    id: "crs-react",
    title: "Modern React & Next.js Development Course",
    category: "Courses",
    description: "React 18, Next.js App Router, Tailwind CSS, TypeScript, Server Components, and cloud production deployment.",
    url: "/courses#react-nextjs",
    keywords: ["react", "nextjs", "frontend", "hooks", "tailwind", "typescript", "web development", "course"],
  },
  {
    id: "crs-marketing",
    title: "Performance Digital Marketing & Growth Strategy",
    category: "Courses",
    description: "Multi-channel digital marketing, technical SEO, Google Ads, Meta advertising, GA4 web analytics, and funnel growth.",
    url: "/courses#digital-marketing",
    keywords: ["digital marketing", "seo", "google ads", "meta ads", "ga4", "analytics", "marketing", "growth", "course"],
  },
  {
    id: "crs-security",
    title: "Cyber Security & Ethical Hacking Fundamentals",
    category: "Courses",
    description: "Network security, OWASP Top 10 vulnerability assessment, penetration testing, and CERT-In compliance.",
    url: "/courses#cyber-security",
    keywords: ["cyber security", "ethical hacking", "owasp", "penetration testing", "cert-in", "network security", "course"],
  },
  {
    id: "crs-embedded",
    title: "Embedded Systems & IoT Hardware Engineering",
    category: "Courses",
    description: "C/C++ microcontroller programming (ESP32, ARM Cortex), circuit PCB layout design, sensors, and MQTT IoT protocols.",
    url: "/courses#embedded-iot",
    keywords: ["embedded", "iot", "microcontroller", "esp32", "arm cortex", "c++", "sensors", "mqtt", "pcb", "course"],
  },

  // Pricing & Plans
  {
    id: "prc-starter",
    title: "Starter Plan — ₹7,999",
    category: "Pricing",
    description: "Single-page scrollable web presence for micro-businesses, personal portfolios, and event sites.",
    url: "/pricing#starter",
    keywords: ["starter plan", "7999", "7,999", "static website", "single page", "micro business", "pricing"],
  },
  {
    id: "prc-basic",
    title: "Basic Plan — ₹14,999",
    category: "Pricing",
    description: "Structured 5-page custom static website with image gallery, SSL, and Google Analytics 4.",
    url: "/pricing#basic",
    keywords: ["basic plan", "14999", "14,999", "5 pages", "custom design", "gallery", "ssl", "pricing"],
  },
  {
    id: "prc-standard",
    title: "Standard Plan — ₹22,999",
    category: "Pricing",
    description: "Comprehensive 8-page static site with micro-animations, FAQ, and rich Schema markup.",
    url: "/pricing#standard",
    keywords: ["standard plan", "22999", "22,999", "8 pages", "micro-animations", "schema", "faq", "pricing"],
  },
  {
    id: "prc-pro",
    title: "Professional CMS Plan — ₹34,999",
    category: "Pricing",
    description: "CMS-powered dynamic website (WordPress / Strapi) with self-managed blog, gallery, and admin training.",
    url: "/pricing#professional",
    keywords: ["professional plan", "34999", "34,999", "cms", "wordpress", "strapi", "blog", "admin panel", "pricing"],
  },
  {
    id: "prc-biz",
    title: "Business E-Commerce Plan — ₹64,999+",
    category: "Pricing",
    description: "Full e-commerce platform with payment gateway (Razorpay/Stripe), cart, user accounts, and product catalog.",
    url: "/pricing#business",
    keywords: ["business plan", "64999", "64,999", "e-commerce", "payment gateway", "cart", "products", "razorpay", "pricing"],
  },
  {
    id: "prc-custom",
    title: "Custom Web Application — ₹99,999+",
    category: "Pricing",
    description: "Bespoke web applications, SaaS platforms, custom APIs, database design, and cloud CI/CD deployment.",
    url: "/pricing#custom-app",
    keywords: ["web application", "99999", "99,999", "custom engineering", "saas", "cloud", "ci/cd", "pricing"],
  },
  {
    id: "prc-addons",
    title: "Add-On Services & Module Fees",
    category: "Pricing",
    description: "Additional pages (₹1,499), site migration (₹3,999), security hardening (₹3,999), speed optimisation (₹4,999).",
    url: "/pricing",
    keywords: ["add-ons", "additional page", "migration", "security hardening", "speed optimization", "logo design"],
  },

  // Company
  {
    id: "cmp-about",
    title: "About RADIECE PRIVATE LIMITED",
    category: "Company",
    description: "Our company overview, founding principles, engineering delivery mindset, and corporate governance.",
    url: "/about#who-we-are",
    keywords: ["about us", "who we are", "principles", "philosophy", "vision", "mission"],
  },
  {
    id: "cmp-statutory",
    title: "Statutory Credentials & Corporate Identity (CIN)",
    category: "Company",
    description: "RADIECE PRIVATE LIMITED • CIN: U58200KL2026PTC104862 • GSTIN: 32AAQCR1162B1ZR • Incorporation 21-07-2026.",
    url: "/about#statutory",
    keywords: ["registered office", "cin", "u58200kl2026ptc104862", "gstin", "32aaqcr1162b1zr", "statutory", "mca"],
  },
  {
    id: "cmp-office",
    title: "Operational Base & Registered Office",
    category: "Company",
    description: "Suite No. 1034, Heiley Offices, 2nd Floor KC Arcade, Near TV Centre, CSEZ P.O, Kakkanad, Ernakulam - 682037, Kerala.",
    url: "/about#registered-office",
    keywords: ["address", "office", "kakkanad", "ernakulam", "kerala", "location", "heiley offices"],
  },
  {
    id: "cmp-pillars",
    title: "6 Core Practice Pillars",
    category: "Company",
    description: "IT & ITeS, SaaS Digital Platforms, Applied Hardware R&D, Robotics & Automation, Security Systems, Technology Education.",
    url: "/about#service-pillars",
    keywords: ["practice pillars", "verticals", "capabilities", "core practices"],
  },
  {
    id: "cmp-careers",
    title: "Careers — UI/UX Designer & Developer (Remote)",
    category: "Company",
    description: "Apply for UI/UX Designer & Developer (1–2 yrs exp, Remote, ₹13k–₹20k/mo). Design SaaS interfaces, Figma mockups, and front-end code.",
    url: "/careers",
    keywords: ["careers", "jobs", "ui/ux", "ui ux designer", "frontend developer", "figma", "tailwind css", "remote jobs", "work from home", "hiring", "apply online"],
  },
  {
    id: "cmp-contact",
    title: "Contact Us & Technical Inquiry Desk",
    category: "Company",
    description: "Get in touch with RADIECE engineering coordinators, call +91 95675 51437 or submit custom project scopes.",
    url: "/contact",
    keywords: ["contact", "email", "phone", "address", "inquiry", "connect", "reach us", "kakkanad", "ernakulam"],
  },

  // Policies
  {
    id: "pol-security",
    title: "Security & Vulnerability Reporting Center",
    category: "Policies",
    description: "Submit security findings, response SLAs (24h triage), PGP email backup, and responsible disclosure policy.",
    url: "/security",
    keywords: ["security", "bug report", "vulnerability", "bounty", "sla", "cert-in", "breach"],
  },
  {
    id: "pol-amc",
    title: "Annual Maintenance Contracts (AMC) Details",
    category: "Policies",
    description: "Structured website and software maintenance tiers, backups, security patching, and SLA support hours.",
    url: "/amc",
    keywords: ["amc", "annual maintenance", "maintenance contract", "sla", "support", "backups"],
  },
  {
    id: "pol-terms",
    title: "Terms & Conditions",
    category: "Policies",
    description: "Master service agreement, payment schedules, intellectual property rights, and cancellation policies.",
    url: "/terms",
    keywords: ["terms", "conditions", "service agreement", "legal", "payment terms", "ip rights"],
  },
  {
    id: "pol-privacy",
    title: "Privacy Policy (DPDP Act 2023)",
    category: "Policies",
    description: "Data privacy compliance, personal data collection, user rights, and IT Act SPDI Rules.",
    url: "/privacy",
    keywords: ["privacy policy", "dpdp act", "personal data", "spdi rules", "user rights", "cookies"],
  },
  {
    id: "pol-storage",
    title: "Data Storage & Retention Policy",
    category: "Policies",
    description: "Data retention schedules, CERT-In 180-day log mandates, AES-256 encryption, and backup security.",
    url: "/data-storage",
    keywords: ["data storage", "data retention", "cert-in", "encryption", "aes-256", "backups", "log retention"],
  },

  // Technical Blog & Publications
  {
    id: "blog-rabuos",
    title: "Architecting RaBuOS: Building a Multi-Tenant Enterprise OS with Bun & TypeScript",
    category: "Blog & Publications",
    description: "Deep dive into deterministic state management, sub-millisecond route dispatch, and automated statutory compliance on Bun.",
    url: "/blog/architecting-rabuos-multi-tenant-enterprise-os-bun-typescript",
    keywords: ["blog", "rabuos", "bun", "typescript", "multi-tenant", "enterprise os", "architecture", "dag"],
  },
  {
    id: "blog-lorawan",
    title: "Industrial IoT & LoRaWAN: Real-Time Sensor Telemetry from Edge to Cloud",
    category: "Blog & Publications",
    description: "Engineering low-power, 10km wireless telemetry mesh networks using ESP32 and Semtech SX1262 LoRa gateways.",
    url: "/blog/industrial-iot-lorawan-sensor-telemetry-edge-to-cloud",
    keywords: ["blog", "iot", "lorawan", "esp32", "hardware", "telemetry", "wireless", "sensors", "iiot"],
  },
  {
    id: "blog-dpdp",
    title: "DPDP Act 2023 Technical Compliance Guide: Zero-Trust Data Architecture",
    category: "Blog & Publications",
    description: "Practical guide to implementing the Digital Personal Data Protection Act 2023 consent artifacts and encryption standards.",
    url: "/blog/dpdp-act-2023-technical-compliance-zero-trust-architecture",
    keywords: ["blog", "dpdp act", "data protection", "privacy", "security", "zero trust", "cert-in", "compliance"],
  },
  {
    id: "blog-embedded",
    title: "Modern Embedded Systems: Transitioning from Legacy 8-bit to ESP32 & ARM Cortex",
    category: "Blog & Publications",
    description: "Why modern electronics are leaving 8-bit MCUs for 32-bit dual-core architectures with FreeRTOS multitasking.",
    url: "/blog/modern-embedded-systems-transitioning-8bit-to-esp32-arm-cortex",
    keywords: ["blog", "embedded systems", "esp32", "arm cortex", "freertos", "microcontroller", "firmware"],
  },

  // News & Corporate Dispatches
  {
    id: "news-incorporation",
    title: "Official Incorporation of RADIECE PRIVATE LIMITED under Ministry of Corporate Affairs (MCA)",
    category: "News & Dispatches",
    description: "Incorporation on 21 July 2026 under the Companies Act, 2013 (CIN: U58200KL2026PTC104862, GSTIN: 32AAQCR1162B1ZR) in Kakkanad, Ernakulam.",
    url: "/news/official-incorporation-radiece-private-limited-21-july-2026",
    keywords: ["news", "incorporation", "statutory notice", "mca", "cin", "21 july 2026", "corporate milestone"],
  },
  {
    id: "news-rabuos-roadmap",
    title: "Future Vision: Development Roadmap & Q4 Public Beta for RaBuOS Platform",
    category: "News & Dispatches",
    description: "RaBuOS in active development on Bun runtime, with Public Beta release scheduled for Q4 2026 (~Dec).",
    url: "/news/rabuos-enterprise-platform-development-q4-beta",
    keywords: ["news", "rabuos", "beta", "roadmap", "q4 2026", "platform update", "future vision"],
  },
  {
    id: "news-lab-blueprint",
    title: "Future Vision: Planned Deep-Tech Embedded Hardware & IoT Prototyping Lab at Kakkanad",
    category: "News & Dispatches",
    description: "Planned setup of specialized electronic prototyping facility for ESP32/ARM PCB assembly and 865 MHz ISM wireless testing.",
    url: "/news/planned-deep-tech-embedded-iot-prototyping-facility-kakkanad",
    keywords: ["news", "hardware lab", "robotics", "kakkanad", "prototyping", "iot", "engineering dispatch", "future vision"],
  },
  {
    id: "news-education-curricula",
    title: "Future Vision: Industry-Aligned Engineering Education & Curriculum Blueprint",
    category: "News & Dispatches",
    description: "Proposed curriculum syllabus frameworks for Python Full-Stack, React/Next.js, and Embedded IoT engineering tracks.",
    url: "/news/planned-technical-education-engineering-curricula-rollout",
    keywords: ["news", "training", "curriculum", "python", "embedded", "company release", "future vision"],
  },
];

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SearchModal({ isOpen, onClose }: SearchModalProps) {
  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      setQuery("");
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (isOpen) onClose();
        else {
          const trigger = document.getElementById("search-trigger");
          if (trigger) trigger.click();
        }
      } else if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const normalizedQuery = query.toLowerCase().trim();

  const pageStatuses = getPageStatuses();
  const flags = getFeatureFlags();

  const results = searchableItems.filter((item) => {
    const rawPath = (item.url.replace(/^\//, "").split("#")[0] ?? "").split("/")[0] ?? "";
    const targetRoute = "/" + (rawPath || "");
    if (targetRoute === "/careers" && !flags.enableCareers) return false;
    if (targetRoute === "/pricing" && !flags.enablePricingCalculator) return false;
    const status = pageStatuses.find((p) => p.path === targetRoute);
    if (status && status.status === "draft") return false;

    const matchesCategory = categoryFilter === "all" || item.category.toLowerCase() === categoryFilter;
    if (!normalizedQuery) return matchesCategory;

    const matchesTitle = item.title.toLowerCase().includes(normalizedQuery);
    const matchesDesc = item.description.toLowerCase().includes(normalizedQuery);
    const matchesKeywords = item.keywords.some((k) => k.toLowerCase().includes(normalizedQuery));

    return matchesCategory && (matchesTitle || matchesDesc || matchesKeywords);
  });

  const handleSelect = (url: string) => {
    document.body.style.overflow = "";
    onClose();
    setTimeout(() => {
      navigate(url);
    }, 10);
  };

  return (
    <div
      className="no-print fixed inset-0 z-50 flex items-start justify-center pt-16 sm:pt-24 px-4 bg-slate-950/70 backdrop-blur-sm animate-fadeIn"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="search-modal-title"
    >
      <div
        className="w-full max-w-2xl bg-[#101c2a] border border-white/20 rounded-2xl shadow-2xl overflow-hidden flex flex-col text-slate-100 max-h-[80vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Input Header */}
        <div className="p-4 border-b border-white/10 flex items-center gap-3 bg-[#0a121c]">
          <svg className="w-5 h-5 text-primary shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search services, courses, pricing plans, publications..."
            className="w-full bg-transparent text-white placeholder-slate-400 text-sm sm:text-base focus:outline-none"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="text-xs text-slate-400 hover:text-white bg-white/10 px-2 py-1 rounded transition-colors"
            >
              Clear
            </button>
          )}
          <button
            onClick={onClose}
            className="text-xs text-slate-400 hover:text-white bg-white/10 px-2.5 py-1 rounded font-mono transition-colors"
          >
            ESC
          </button>
        </div>

        {/* Category Pills */}
        <div className="px-4 py-2.5 bg-[#0a121c] border-b border-white/10 flex flex-wrap items-center gap-1.5 text-xs">
          {[
            { id: "all", label: "All Results" },
            { id: "services", label: "Services" },
            { id: "courses", label: "Courses" },
            { id: "pricing", label: "Pricing" },
            { id: "company", label: "Company" },
            { id: "policies", label: "Policies" },
          ].map((cat) => (
            <button
              key={cat.id}
              onClick={() => setCategoryFilter(cat.id)}
              className={`px-3 py-1 rounded-full text-xs transition-colors cursor-pointer ${
                categoryFilter === cat.id
                  ? "bg-primary text-white font-semibold"
                  : "bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Search Results List */}
        <div className="p-3 sm:p-4 overflow-y-auto flex-1 space-y-2">
          {results.length > 0 ? (
            results.map((item) => (
              <a
                key={item.id}
                href={item.url}
                onClick={(e) => {
                  e.preventDefault();
                  handleSelect(item.url);
                }}
                className="block p-3.5 rounded-xl bg-white/5 hover:bg-primary/10 border border-white/5 hover:border-primary/40 transition-all duration-150 group cursor-pointer"
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-xs font-semibold text-brand-light group-hover:text-primary transition-colors">
                    {item.title}
                  </span>
                  <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-white/10 text-slate-300">
                    {item.category}
                  </span>
                </div>
                <p className="text-xs text-slate-300 line-clamp-2 leading-relaxed">
                  {item.description}
                </p>
              </a>
            ))
          ) : (
            <div className="text-center py-12 text-slate-400">
              <svg className="w-10 h-10 mx-auto text-slate-600 mb-3" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
              <p className="text-sm font-semibold text-slate-300 mb-1">No matching results found</p>
              <p className="text-xs text-slate-500">Try searching for keywords like "React", "Python", "Pricing", "Security", or "AMC".</p>
            </div>
          )}
        </div>

        {/* Footer Hint */}
        <div className="p-3 bg-[#0a121c] border-t border-white/10 text-center text-xs text-slate-400 flex items-center justify-between px-5">
          <span>Quick Search Facility</span>
          <span className="hidden sm:inline font-mono text-[11px] text-slate-500">Press Ctrl+K anytime to search</span>
        </div>
      </div>
    </div>
  );
}
